import * as React from 'react';
import { Input, type InputProps } from './input';
import { ValidationStatusIcon, type CheckStatus } from './validation-status';

const SWISS_AVS_LENGTH = 13;

/** Strip all non-digits. */
function stripAvs(value: string): string {
  return value.replace(/\D/g, '');
}

/** Normalize an AVS value to raw digits. */
function normalizeAvs(value: string | undefined): string {
  return stripAvs(value ?? '').slice(0, SWISS_AVS_LENGTH);
}

/** Format a raw AVS number into 756.1234.5678.97 form. */
function formatAvs(raw: string): string {
  const normalized = normalizeAvs(raw);
  const parts = [
    normalized.slice(0, 3),
    normalized.slice(3, 7),
    normalized.slice(7, 11),
    normalized.slice(11, 13),
  ].filter(Boolean);
  return parts.join('.');
}

/** Validate a Swiss AVS/AHV number using the EAN-13 checksum. */
function validateSwissAvs(raw: string): boolean {
  if (!/^\d{13}$/.test(raw)) return false;
  if (!raw.startsWith('756')) return false;

  let sum = 0;
  for (let index = 0; index < SWISS_AVS_LENGTH - 1; index += 1) {
    const digit = Number(raw[index]);
    sum += digit * (index % 2 === 0 ? 1 : 3);
  }

  const checkDigit = (10 - (sum % 10)) % 10;
  return checkDigit === Number(raw[SWISS_AVS_LENGTH - 1]);
}

export interface AvsInputProps extends Omit<
  InputProps,
  'value' | 'onChange' | 'type' | 'maxLength' | 'endAdornment'
> {
  /** Raw AVS value without separators (e.g. "7569217076985") */
  value?: string;
  /** Called with the raw AVS string */
  onChange?: (value: string) => void;
}

const AvsInput = React.forwardRef<HTMLInputElement, AvsInputProps>(
  ({ value: controlledValue, onChange, ...props }, ref) => {
    const [internalValue, setInternalValue] = React.useState('');
    const isControlled = controlledValue !== undefined;
    const rawValue = normalizeAvs(
      isControlled ? controlledValue : internalValue,
    );

    const [status, setStatus] = React.useState<CheckStatus>({ type: 'idle' });

    React.useEffect(() => {
      if (rawValue.length === SWISS_AVS_LENGTH) {
        setStatus(
          validateSwissAvs(rawValue) ? { type: 'valid' } : { type: 'invalid' },
        );
        return;
      }

      setStatus({ type: 'idle' });
    }, [rawValue]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const nextValue = normalizeAvs(e.target.value);

      if (!isControlled) setInternalValue(nextValue);
      onChange?.(nextValue);
    };

    const statusIcon =
      status.type !== 'idle' ? (
        <ValidationStatusIcon status={status} />
      ) : undefined;

    return (
      <Input
        ref={ref}
        value={formatAvs(rawValue)}
        onChange={handleChange}
        placeholder="756.1234.5678.97"
        inputMode="numeric"
        autoComplete="off"
        spellCheck={false}
        aria-invalid={status.type === 'invalid' || undefined}
        endAdornment={statusIcon}
        {...props}
        className={props.className}
        style={{
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '0.05em',
          ...props.style,
        }}
      />
    );
  },
);
AvsInput.displayName = 'AvsInput';

export { AvsInput, normalizeAvs, validateSwissAvs };
